import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  proyectosApi,
  municipiosApi,
  oficinasApi,
  getApiError,
} from "../api/catalogosApi";


const clean = (value) =>
  String(value || "").trim();


const normalize = (value) =>
  clean(value)
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/\s+/g, " ")
    .toUpperCase();


export function useInventarioCatalogos() {
  const [
    proyectos,
    setProyectos
  ] = useState([]);

  const [
    municipios,
    setMunicipios
  ] = useState([]);

  const [
    oficinas,
    setOficinas
  ] = useState([]);

  const [
    loading,
    setLoading
  ] = useState(true);

  const [
    error,
    setError
  ] = useState("");


  // ==========================================================
  // CARGA CENTRAL
  // ==========================================================

  const recargar =
    useCallback(async () => {

      setLoading(true);
      setError("");

      try {
        const [
          proyectosData,
          municipiosData,
          oficinasData,
        ] = await Promise.all([
          proyectosApi.listar(false),

          municipiosApi.listar({
            includeInactive: false,
          }),

          oficinasApi.listar({
            includeInactive: false,
          }),
        ]);


        const proyectosLimpios =
          Array.isArray(proyectosData)
            ? proyectosData.filter(
                (item) =>
                  item &&
                  item.activo !== false &&
                  clean(item.nombre)
              )
            : [];


        const municipiosLimpios =
          Array.isArray(municipiosData)
            ? municipiosData.filter(
                (item) =>
                  item &&
                  item.activo !== false &&
                  clean(item.nombre)
              )
            : [];


        /*
         * Una oficina valida para Inventario debe
         * tener municipioId.
         *
         * Esto excluye registros legacy huerfanos
         * sin borrar nada del backend.
         */
        const oficinasLimpias =
          Array.isArray(oficinasData)
            ? oficinasData.filter(
                (item) =>
                  item &&
                  item.activo !== false &&
                  item.municipioId &&
                  clean(item.nombre)
              )
            : [];


        proyectosLimpios.sort(
          (a, b) =>
            clean(a.nombre).localeCompare(
              clean(b.nombre),
              "es"
            )
        );


        municipiosLimpios.sort(
          (a, b) =>
            clean(a.nombre).localeCompare(
              clean(b.nombre),
              "es"
            )
        );


        oficinasLimpias.sort(
          (a, b) => {

            const municipioCompare =
              clean(
                a.municipioNombre
              ).localeCompare(
                clean(
                  b.municipioNombre
                ),
                "es"
              );

            if (municipioCompare !== 0) {
              return municipioCompare;
            }

            return clean(
              a.nombre
            ).localeCompare(
              clean(
                b.nombre
              ),
              "es"
            );
          }
        );


        setProyectos(
          proyectosLimpios
        );

        setMunicipios(
          municipiosLimpios
        );

        setOficinas(
          oficinasLimpias
        );


        return {
          proyectos:
            proyectosLimpios,

          municipios:
            municipiosLimpios,

          oficinas:
            oficinasLimpias,
        };

      } catch (err) {

        console.error(
          "[useInventarioCatalogos] Error:",
          err
        );

        setError(
          getApiError(err)
        );

        throw err;

      } finally {

        setLoading(false);
      }

    }, []);


  // ==========================================================
  // CARGA INICIAL
  // ==========================================================

  useEffect(() => {

    let mounted = true;


    const run =
      async () => {

        try {

          const result =
            await recargar();

          if (!mounted) {
            return;
          }

          console.info(
            "[InventarioCatalogos] cargados",
            {
              proyectos:
                result.proyectos.length,

              municipios:
                result.municipios.length,

              oficinas:
                result.oficinas.length,
            }
          );

        } catch {

          /*
           * El error ya fue manejado dentro
           * de recargar().
           */
        }
      };


    run();


    return () => {
      mounted = false;
    };

  }, [
    recargar,
  ]);


  // ==========================================================
  // MUNICIPIOS POR ID
  // ==========================================================

  const municipiosPorId =
    useMemo(() => {

      const map =
        new Map();

      for (
        const municipio
        of municipios
      ) {

        map.set(
          municipio.id,
          municipio
        );
      }

      return map;

    }, [
      municipios,
    ]);


  // ==========================================================
  // MUNICIPIOS POR NOMBRE NORMALIZADO
  // ==========================================================

  const municipiosPorNombre =
    useMemo(() => {

      const map =
        new Map();

      for (
        const municipio
        of municipios
      ) {

        map.set(
          normalize(
            municipio.nombre
          ),
          municipio
        );
      }

      return map;

    }, [
      municipios,
    ]);


  // ==========================================================
  // OFICINAS AGRUPADAS POR municipioId
  // ==========================================================

  const oficinasPorMunicipioId =
    useMemo(() => {

      const map =
        new Map();


      for (
        const oficina
        of oficinas
      ) {

        const municipioId =
          oficina.municipioId;

        if (!municipioId) {
          continue;
        }


        if (
          !map.has(
            municipioId
          )
        ) {
          map.set(
            municipioId,
            []
          );
        }


        map
          .get(
            municipioId
          )
          .push(
            oficina
          );
      }


      for (
        const lista
        of map.values()
      ) {

        lista.sort(
          (a, b) =>
            clean(
              a.nombre
            ).localeCompare(
              clean(
                b.nombre
              ),
              "es"
            )
        );
      }


      return map;

    }, [
      oficinas,
    ]);


  // ==========================================================
  // BUSCAR MUNICIPIO POR NOMBRE
  //
  // Inventario actual guarda el nombre,
  // por eso soportamos la busqueda por nombre.
  // ==========================================================

  const getMunicipioByNombre =
    useCallback(
      (nombre) => {

        if (!nombre) {
          return null;
        }

        return (
          municipiosPorNombre.get(
            normalize(nombre)
          ) || null
        );

      },
      [
        municipiosPorNombre,
      ]
    );


  // ==========================================================
  // OFICINAS POR ID DE MUNICIPIO
  // ==========================================================

  const getOficinasByMunicipioId =
    useCallback(
      (municipioId) => {

        if (!municipioId) {
          return [];
        }

        return (
          oficinasPorMunicipioId.get(
            municipioId
          ) || []
        );

      },
      [
        oficinasPorMunicipioId,
      ]
    );


  // ==========================================================
  // OFICINAS POR NOMBRE DE MUNICIPIO
  //
  // Este sera el metodo mas util para el InventarioForm
  // porque actualmente form.municipio contiene el nombre.
  // ==========================================================

  const getOficinasByMunicipioNombre =
    useCallback(
      (municipioNombre) => {

        const municipio =
          getMunicipioByNombre(
            municipioNombre
          );

        if (!municipio) {
          return [];
        }

        return (
          oficinasPorMunicipioId.get(
            municipio.id
          ) || []
        );

      },
      [
        getMunicipioByNombre,
        oficinasPorMunicipioId,
      ]
    );


  return {
    loading,
    error,

    proyectos,
    municipios,
    oficinas,

    municipiosPorId,
    oficinasPorMunicipioId,

    getMunicipioByNombre,
    getOficinasByMunicipioId,
    getOficinasByMunicipioNombre,

    recargar,
  };
}


export default useInventarioCatalogos;